import { computed, watch, onScopeDispose } from 'vue';

import { defineStore } from 'pinia';

import { useAbility } from '@casl/vue';

import {
  getItemFromLocalStorage,
  getItemFromSessionStorage,
  setItemToSessionStorage,
} from '@/helpers/localStorageServices';
import { initialAbility } from '@/plugins/casl/ability';
import * as backendAuth from '@/services/auth/backend-auth.service';
import { bpSelection } from '@/services/auth/bpSelection';
import { session } from '@/services/auth/session';
import { tokenResolver } from '@/services/auth/tokenResolver';
import { useAccountStore } from '@/stores/account.js';

// owner/admin scope → manage:all; otherwise read Public+User. Same rule as the legacy store.
const deriveAbilities = (scopes = []) =>
  scopes.includes('owner') || scopes.includes('admin')
    ? [{ action: 'manage', subject: 'all' }]
    : [
      { action: 'read', subject: 'Public' },
      { action: 'read', subject: 'User' },
    ];

export const useAuthStore = defineStore('auth', () => {
  const ability = useAbility();
  const accountStore = useAccountStore();

  // On a hard refresh accountStore.currentBusinessProfile is null until syncBusinessProfiles
  // runs, but the last-used BP is in sessionStorage. Fall back to it so the hydrated per-BP
  // token, CASL scopes and analytics gate are available immediately (matches old behavior).
  const storedBpId = () =>
    getItemFromSessionStorage('currentBusinessProfile')?.id ??
    getItemFromLocalStorage('currentBusinessProfile')?.id ??
    null;
  const currentBPId = computed(() => accountStore.currentBusinessProfile?.id ?? storedBpId());

  // Which token/scopes serve a BP (default profile → session token, others → per-BP app token)
  // is owned by tokenResolver — the store only exposes its synchronous snapshots to
  // computeds/CASL, which cannot await.

  // Keep CASL in sync with the current BP's scopes (replaces the inline block that lived
  // in the old getAppAccessToken). Fires on auth/profile/scope changes.
  const syncAbilities = () => {
    const abilities = session.isAuthenticated.value
      ? deriveAbilities(currentBPId.value ? tokenResolver.scopesFor(currentBPId.value) : [])
      : initialAbility;
    ability.update(abilities);
  };

  watch(
    () => [
      session.isAuthenticated.value,
      currentBPId.value,
      tokenResolver.scopesFor(currentBPId.value),
      // Track the session token so CASL re-derives when a refresh re-scopes the default profile.
      session.getSessionAccessToken(),
    ],
    syncAbilities,
    { immediate: true, deep: true },
  );

  // Runs after the logout redirect: reset CASL and clear the account store (profiles,
  // currentBusinessProfile + its session/local storage) so a logged-out session leaves
  // no stale BP behind (which storedBpId() would otherwise resurrect).
  const unsubscribeSignOut = session.onSignOut(() => {
    ability.update(initialAbility);
    accountStore.clearAccount();
  });
  // Detach on scope dispose so a re-created store (HMR) doesn't stack duplicate listeners.
  onScopeDispose(unsubscribeSignOut);

  // Current-BP reactive views (analytics grids watch authStore.accessToken to reload on BP switch).
  const accessToken = computed(() =>
    currentBPId.value ? tokenResolver.tokenFor(currentBPId.value) : null,
  );
  const scopes = computed(() =>
    currentBPId.value ? tokenResolver.scopesFor(currentBPId.value) : null,
  );

  const resolveBpId = (bpId) => {
    if (bpId) return bpId;
    // Synchronous BP-selection intent (seeded at bootstrap, updated eagerly on switch) — replaces
    // the direct window.location read now that the URL is a deferred coordinator reflection.
    const intentId = bpSelection.get();
    if (intentId) return intentId;
    return getItemFromSessionStorage('currentBusinessProfile')?.id ?? currentBPId.value ?? null;
  };

  // Resolve a BP id and mint/return the app token for that profile (default profile → the
  // session token itself; others → /auth/switch-profile mint, deduped + locked in profileTokens).
  const getAppAccessToken = async (bpId) => {
    const id = resolveBpId(bpId);
    if (!id) return null;
    if (!session.hasSession()) return null;
    return tokenResolver.fetchInitial(id);
  };

  // Explicit user-initiated profile switch: always calls /auth/switch-profile (bypassing the app
  // token cache) so the backend re-issues the scoped token and persists the last-used profile.
  const switchToProfile = async (bpId) => {
    const id = resolveBpId(bpId);
    if (!id) return null;
    if (!session.hasSession()) return null;
    return tokenResolver.switchTo(id);
  };

  // Safety net (preserves the old store's currentBusinessProfileId watcher): mint the app
  // token when a valid BP becomes current and its token is missing/expired. accountStore
  // already validates the current profile, so no existence check is needed here. Deduped
  // with account.js's own getAppAccessToken calls via the per-BP refresh lock. The resolver
  // routes the default profile to the session token, so no per-BP duplicate can be minted here.
  watch(
    currentBPId,
    (id) => {
      // While the session is pre-profile (fresh registration — no businessProfileId claim),
      // adoption of the first profile is owned by claimDefaultProfile (createBusinessProfile /
      // restore heal / the resolver gate). Firing a per-BP mint here would race that adoption
      // and risk duplicating the session's refresh chain (WP-981), so skip until the session
      // is scoped. Legitimate profile switches always run on an already-scoped session.
      if (
        id &&
        session.isAuthenticated.value &&
        session.getSessionBpId() != null &&
        tokenResolver.isExpired(id)
      ) {
        getAppAccessToken(id).catch((e) => console.error('[auth] auto app-token fetch failed:', e));
      }
    },
    { immediate: true },
  );

  // Federated login: map the UI provider name to the backend slug, then redirect to the
  // backend-built Hosted-UI authorize URL.
  const providerSlug = { Google: 'google', LoginWithAmazon: 'amazon', SignInWithApple: 'apple' };
  const loginWithProvider = async (provider, flow = 'login', { returnTo: explicitReturnTo } = {}) => {
    const slug = providerSlug[provider] || String(provider).toLowerCase();
    // Preserve the deep-link the user started from (planted as ?to= on /login by the
    // router guard) across the Cognito round-trip; oauth-landing reads it back after the
    // exchange. Set unconditionally so a fresh login without ?to= clears any stale value.
    // A retry from /oauth-landing has no ?to= in its URL, so it passes the stashed one.
    const returnTo =
      explicitReturnTo !== undefined
        ? explicitReturnTo
        : new URLSearchParams(window.location.search).get('to');
    setItemToSessionStorage('postAuthReturnTo', returnTo);
    // Remember the attempt so /oauth-landing can offer a one-click retry even when an older
    // backend reports only `error=oauth_failed` (no provider / flow in the redirect).
    setItemToSessionStorage('oauthAttempt', { provider: slug, flow });
    window.location.href = await backendAuth.oauthStartUrl(slug, flow);
  };

  // True when the account can sign in with a password (has the COGNITO provider).
  // Derived from /users/me providers[]; used to gate the password section.
  const hasPasswordLogin = computed(() =>
    (session.user.value?.providers ?? []).some((p) => p.provider === 'COGNITO'),
  );

  // Connect an additional login method: redirect to the backend-built authorize URL.
  const connectProvider = async (provider) => {
    // Remember which provider we're linking so the connect return can verify it was
    // actually linked — Cognito can report connect=failed on the email-alias race even
    // when the link succeeded.
    setItemToSessionStorage('connectingProvider', provider);
    window.location.href = await backendAuth.connectProviderUrl(provider);
  };

  return {
    // Real state
    isAuthenticated: session.isAuthenticated,
    isAuthLoading: session.isAuthLoading,
    isInitializing: session.isInitializing,
    user: session.user,
    isSocialLogin: session.isSocialLogin,
    hasPasswordLogin,

    // Actions
    login: session.login,
    verifyMfa: session.verifyMfa,
    recoveryVerify: session.recoveryVerify,
    register: session.register,
    confirmRegistration: session.confirmRegistration,
    loginWithProvider,
    connectProvider,
    disconnectProvider: backendAuth.disconnectProvider,
    logout: session.logout,
    logoutAll: backendAuth.logoutAll,
    listSessions: backendAuth.listSessions,
    revokeSession: backendAuth.revokeSession,
    revokeOtherSessions: backendAuth.revokeOtherSessions,
    getMfaStatus: backendAuth.getMfaStatus,
    mfaTotpStart: backendAuth.mfaTotpStart,
    mfaTotpVerify: backendAuth.mfaTotpVerify,
    mfaEmailEnable: backendAuth.mfaEmailEnable,
    mfaResetStart: backendAuth.mfaResetStart,
    mfaResetVerify: backendAuth.mfaResetVerify,
    mfaDisable: backendAuth.mfaDisable,
    resetPassword: session.resetPassword,
    confirmPasswordReset: session.confirmPasswordReset,
    updatePassword: session.updatePassword,
    setPassword: session.setPassword,
    getUserAttributes: async () => session.user.value,
    updateUserProfile: session.updateProfile,
    refreshUser: session.refreshUser,
    resendSignUpCode: session.resendSignUpCode,
    handleLoginWithAmazonAds: session.handleLoginWithAmazonAds,
    handleLoginWithAmazonSellingPartner: session.handleLoginWithAmazonSellingPartner,

    // Backward-compat surface (kept so existing consumers don't change in Tier 1)
    loggedIn: session.isAuthenticated,
    isInitializingAuth: session.isInitializing, // VerticalNav / HeaderAccountInfo read via storeToRefs
    initializeSession: session.restore, // oauth-callback + register email-verification call this
    accessToken,
    scopes,
    oauth2AccessToken: computed(() => null),
    oauth2IdToken: computed(() => null),
    getAppAccessToken,
    switchToProfile,
    // Onboarding only: adopt the just-created FIRST profile into the session token (no-op when
    // the session is already profile-scoped). Logic lives in services/auth (session.js).
    claimDefaultProfile: session.claimDefaultProfile,
    // Public reads of the current app token / scopes for a BP (replace the old
    // getItemFromLocalStorage(getTokenKey(...)) / `accessToken_<bp>` / `scopes_<bp>` patterns).
    // Session-aware: the default profile resolves to the session token/scopes.
    getAppToken: tokenResolver.tokenFor,
    getAppScopes: tokenResolver.scopesFor,
    identifyLogRocketUser: session.identifyLogRocket,
    setAuthenticatedAbilities: syncAbilities,
    getUser: async () => session.user.value,
    clearTokens: () => session.logout({ redirect: false }),

    // Internal (axios interceptor / tokenService only). Straight passthrough to the resolver —
    // it owns the default-vs-per-BP routing, so no path through here can mint a duplicate of the
    // session's refresh chain.
    internal: {
      getAppToken: tokenResolver.tokenFor,
      scopesFor: tokenResolver.scopesFor,
      isExpiredFor: tokenResolver.isExpired,
      refreshFor: tokenResolver.refresh,
      fetchInitialFor: tokenResolver.fetchInitial,
    },
  };
});
